import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form as FinalForm, Field } from 'react-final-form'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import { Grid, Typography, Divider, Button, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import Drawer from '../../control/Drawer'
import Textbox from '../../control/Textbox'
import Scrollbar from '../../control/Scrollbar'
import useMapLayer, { validate, TMapLayer } from '../../hooks/map/useMapLayer'
import FormMapLayerSetting from './FormMapLayerSetting'
import DropdownInput from '../input/DropdownInput'
import { ReactComponent as AddIcon } from '../../icon/map_layer_add.svg'
import { ReactComponent as InfoHelpIcon } from '../../icon/info_help.svg'
import FormMapLayerHelp from './FormMapLayerHelp'
import DialogControl from '../../control/Dialog'
import CustomLayerCard from '../../control/CustomLayerCard'

const initialValues: TMapLayer = {}

type FormMapLayerProps = {}

const FormMapLayer = (props: FormMapLayerProps) => {
    const {
        customMapLayerItem,
        internalCustomMapLayers,
        externalCustomMapLayers,
        onSubmit,
        onSettingExternalMapUrl,
        onDeleteExternalMapUrl,
        onOpenMapHelp,
        onCloseMapLayerSetting,
        onCloseMapLayer,
        onCloseMapHelp,
        onOpacityLayerChange,
        onVisibleLayerChange,
        onVisibleSublayerChange,
        onDialogErrorOKClick,
        openMapLayer,
        openMapLayerSetting,
        openMapHelp,
        openDialogError,
        messageError,
        formRef,
    } = useMapLayer()
    const { t } = useTranslation()
    const classes = useStyles()

    const layerOptions = [
        {
            value: 'layer',
            label: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_LAYER'),
        },
        {
            value: 'wmts',
            label: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMTS'),
        },
        {
            value: 'wms',
            label: t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMS'),
        },
    ]

    const validates = (values: TMapLayer) => {
        const validation = validate(values, t, externalCustomMapLayers.length) || {}
        return validation
    }

    const displayInternalLayer = internalCustomMapLayers
        .slice()
        .sort((a: MapCustomLayer, b: MapCustomLayer) => b.itemIndex - a.itemIndex)

    const displayExternalLayer = externalCustomMapLayers
        .slice()
        .sort((a: MapCustomLayer, b: MapCustomLayer) => b.itemIndex - a.itemIndex)

    const getInternalMapLayer = () => {
        return (
            <StyledContainerMapLayerList>
                {displayInternalLayer?.map((item: MapCustomLayer, index: number) => {
                    return (
                        <CustomLayerCard
                            key={index}
                            displayDeleteButton={false}
                            item={item}
                            onSettingExternalMapUrl={onSettingExternalMapUrl}
                        />
                    )
                })}
            </StyledContainerMapLayerList>
        )
    }

    const getExternalMapLayer = () => {
        return (
            <StyledContainerMapLayerList>
                {displayExternalLayer?.map((item: MapCustomLayer, index: number) => {
                    return (
                        <CustomLayerCard
                            key={index}
                            displayDeleteButton={true}
                            item={item}
                            onSettingExternalMapUrl={onSettingExternalMapUrl}
                            onDeleteExternalMapUrl={onDeleteExternalMapUrl}
                        />
                    )
                })}
            </StyledContainerMapLayerList>
        )
    }

    return (
        <>
            <Drawer
                isOpen={openMapLayer}
                inlineStyle={{ position: 'unset' }}
                hideBackdrop
                overrideClass={{ paper: classes.drawerPaper }}
            >
                <StyledDivContainer>
                    <StyledDivTitle>
                        <Link to="/">
                            <IconButton
                                aria-label="close"
                                onClick={onCloseMapLayer}
                                sx={{
                                    position: 'absolute',
                                    left: 10,
                                    top: 15,
                                }}
                                className={classes.backIcon}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        </Link>
                        <StyledTypography variant="body2">{t('MENU.MAP_LAYER')}</StyledTypography>
                        <IconButton
                            aria-label="open-MapHelp"
                            onClick={onOpenMapHelp}
                            sx={{
                                position: 'absolute',
                                right: 10,
                                top: 13,
                            }}
                            className={classes.backIcon}
                        >
                            <InfoHelpIcon />
                        </IconButton>
                    </StyledDivTitle>
                    <StyledContent>
                        <div style={{ width: '100%', height: '100%' }}>
                            <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                                <StyledContentGroup>
                                    <StyledDivContent>
                                        <StyledDivAddLayer>
                                            <FinalForm
                                                onSubmit={onSubmit}
                                                validate={validates}
                                                initialValues={initialValues}
                                            >
                                                {({ handleSubmit, form, values }) => {
                                                    formRef.current = form
                                                    return (
                                                        <form onSubmit={handleSubmit}>
                                                            <Grid container>
                                                                <Grid item xs={12}>
                                                                    <Field
                                                                        name="layerType"
                                                                        label={t('LABEL.MAP.MAP_LAYER.LAYER_TYPE')}
                                                                        placeholder={t(
                                                                            'LABEL.MAP.MAP_LAYER.LAYER_TYPE_SELECT',
                                                                        )}
                                                                        component={DropdownInput}
                                                                        options={layerOptions}
                                                                        fullWidth
                                                                        required
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={10}>
                                                                    <Field
                                                                        name="layerUrl"
                                                                        label={t('LABEL.MAP.MAP_LAYER.URL')}
                                                                        placeholder={t(
                                                                            'LABEL.MAP.MAP_LAYER.URL_PLACEHOLDER',
                                                                        )}
                                                                        component={Textbox}
                                                                        fullWidth
                                                                        required
                                                                        disabled={!values.layerType}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={1}>
                                                                    <div className={classes.divButton}>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="primary"
                                                                            onClick={handleSubmit}
                                                                            classes={{ root: classes.buttonAdd }}
                                                                            disabled={!values.layerType}
                                                                        >
                                                                            <AddIcon style={{ width: '15px' }} />
                                                                        </Button>
                                                                    </div>
                                                                </Grid>
                                                            </Grid>
                                                        </form>
                                                    )
                                                }}
                                            </FinalForm>
                                        </StyledDivAddLayer>
                                        <Divider className={classes.divider} />

                                        {externalCustomMapLayers?.length > 0 && (
                                            <StyledDivMapSection>
                                                <StyledTypoSection>
                                                    {t('LABEL.MAP.MAP_LAYER.EXTERNAL_MAP')}
                                                </StyledTypoSection>
                                                {getExternalMapLayer()}
                                            </StyledDivMapSection>
                                        )}
                                        {internalCustomMapLayers?.length > 0 && (
                                            <StyledDivMapSection>
                                                <StyledTypoSection>
                                                    {t('LABEL.MAP.MAP_LAYER.INTERNAL_MAP')}
                                                </StyledTypoSection>
                                                {getInternalMapLayer()}
                                            </StyledDivMapSection>
                                        )}

                                       
                                    </StyledDivContent>
                                </StyledContentGroup>
                            </Scrollbar>
                        </div>
                    </StyledContent>
                </StyledDivContainer>
            </Drawer>
            <FormMapLayerSetting
                isOpen={openMapLayerSetting}
                onOpacityLayerChange={onOpacityLayerChange}
                onVisibleLayerChange={onVisibleLayerChange}
                onVisibleSublayerChange={onVisibleSublayerChange}
                onClose={onCloseMapLayerSetting}
                customMapLayerItem={customMapLayerItem}
            />

            <FormMapLayerHelp isOpen={openMapHelp} onClose={onCloseMapHelp} />
            <DialogControl
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={messageError}
                isOpen={openDialogError}
                onClickOk={onDialogErrorOKClick}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default observer(FormMapLayer)

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    backIcon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    divButton: {
        paddingTop: '24px',
        position: 'absolute',
        right: 0,
    },
    buttonAdd: {
        minWidth: '40px !important',
        minHeight: '40px !important',
        padding: '0px !important',
    },
    divider: {
        margin: '20px 0px !important',
    },
}))

const StyledDivContainer = styled('div')(({ theme }) => ({
    width: '350px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: theme.palette.getContrastText('shadow-bottom'),
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
}))

const StyledContent = styled('div')(({ theme }) => ({
    display: 'flex',
    height: `calc(100% - 63px)`,
}))

const StyledContentGroup = styled('div')(({ theme }) => ({
    margin: '20px',
}))

const StyledDivContent = styled('div')(({ theme }) => ({
    marginTop: '10px',
    marginBottom: '20px',
}))

const StyledDivAddLayer = styled('div')(({ theme }) => ({
    width: '100%',
    position: 'relative',
}))

const StyledDivMapSection = styled('div')(({ theme }) => ({
    padding: '5px 0px',
    width: '100%',
}))

const StyledTypoSection = styled(Typography)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    fontWeight: 'bold',
}))

const StyledContainerMapLayerList = styled('div')(({ theme }) => ({
    width: '100%',
    marginTop: '5px',
}))
